import React, { ReactNode, forwardRef, useState } from 'react';
import { CSSProperties } from 'styled-components';
import { useSyncedState } from '@logz-pkg/react-hooks';
import { isNil } from 'lodash';
import { PopoverPlacement } from '../../popover/types';
import { ITooltipProps, Tooltip } from '../../tooltip/Tooltip.component';

export type EllipsisPlacement = PopoverPlacement;

interface IEllipsisProps {
  children: any;
  noTooltip?: boolean;
  maxLines?: number;
  whiteSpace?: CSSProperties['whiteSpace'];
  textAlign?: CSSProperties['textAlign'];
  wordBreak?: 'normal' | 'break-all' | 'keep-all';
  overrideTooltip?: ReactNode;
  openDelayInMs?: ITooltipProps['openDelayInMs'];
  placement?: EllipsisPlacement;
}

const ellipsisDefaultStyle = {
  overflow: 'hidden',
  overflowWrap: 'break-word',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  textAlign: 'start',
};
const maxLinesStyle = numberOfLines => ({
  display: '-webkit-box',
  whiteSpace: 'initial',
  WebkitLineClamp: `${numberOfLines}`,
  WebkitBoxOrient: 'vertical',
});

const SCROLL_SIZE_THRESHOLD = 3;

export const Ellipsis = forwardRef<HTMLSpanElement, IEllipsisProps>(
  (
    { children, noTooltip, maxLines, wordBreak, overrideTooltip, openDelayInMs, textAlign, placement, whiteSpace },
    ref,
  ) => {
    const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
    const [tooltipText, setTooltipText] = useSyncedState(overrideTooltip);

    const ellipsisStyle = {
      ...ellipsisDefaultStyle,
      ...(maxLines ? maxLinesStyle(maxLines) : {}),
      ...(wordBreak ? { wordBreak } : {}),
      ...(textAlign ? { textAlign } : {}),
      ...(whiteSpace ? { whiteSpace } : {}),
    };

    const showTooltip = ({ target }) => {
      if (
        target.scrollWidth > target.clientWidth + SCROLL_SIZE_THRESHOLD ||
        target.scrollHeight > target.clientHeight + SCROLL_SIZE_THRESHOLD
      ) {
        if (isNil(overrideTooltip)) {
          setTooltipText(target.textContent || target.value);
        }

        setShouldShowTooltip(true);
      }
    };

    const hideTooltip = () => {
      setShouldShowTooltip(false);
    };

    const tooltipEvents = (!noTooltip && { onMouseEnter: showTooltip, onMouseLeave: hideTooltip }) || {};

    return (
      <Tooltip
        placement={placement}
        title={shouldShowTooltip ? tooltipText : null}
        openDelayInMs={openDelayInMs}
        disableEvents={noTooltip}
      >
        <span ref={ref}>
          {React.Children.map(children, child => React.cloneElement(child, { style: ellipsisStyle, ...tooltipEvents }))}
        </span>
      </Tooltip>
    );
  },
);

Ellipsis.displayName = 'Ellipsis';
